import { useMemo } from 'react';

import { FlexAligner } from '@scaleaq/scaleui';
import { useTranslation } from 'react-i18next';

import { ALL_SITE_SERVICES, type SiteServiceName } from '@/constants';
import { useSiteServiceListLabel, useSiteServiceSupport } from '@/hooks';
import { useSiteContext } from '@/providers';
import { useConfigStore } from '@/stores';
import { responsive } from '@/utils';

import { toSiteCardItems } from '../siteSelector.utils.ts';
import { groupSitesByServiceSupport } from './selectModalContent.functions.ts';
import { SiteCardsGroup } from './SiteCardsGroup.tsx';
import { useAvailableSites } from './useAvailableSites.ts';

type SelectModalContentProps = {
  /** When false, skip inventory health probes. Defaults to true (this view is on screen). */
  enabled?: boolean;
  /** Exclude the active site only when it would otherwise appear in the unsupported group. */
  hideSelectedUnsupportedSite?: boolean;
  onSelect: (siteId: string) => void;
  /** Services the picker is for; sites supporting any of them are grouped first and the rest are
   * listed as unsupported. Pass a stable (module-scope) array — it keys the grouping memos. */
  services: readonly SiteServiceName[];
  showUnReachableSites?: boolean;
};

export const SelectModalContent = ({
  enabled = true,
  hideSelectedUnsupportedSite = false,
  onSelect,
  services,
  showUnReachableSites = false,
}: SelectModalContentProps) => {
  const { t } = useTranslation();
  const { portableMode } = useConfigStore();
  const { sites, currentSite } = useSiteContext();
  const siteServiceSupport = useSiteServiceSupport(Boolean(portableMode));
  const serviceList = useSiteServiceListLabel(services);

  const cardItems = useMemo(
    () => toSiteCardItems({ sites, selectedSiteId: currentSite?.id, siteServiceSupport }),
    [sites, currentSite?.id, siteServiceSupport]
  );
  const { reachableOrPendingSites, unhealthySites } = useAvailableSites({
    sites: cardItems,
    enabled,
  });

  const supportedSiteIds = useMemo(() => {
    const ids = new Set<string>();
    for (const service of services) {
      for (const siteId of siteServiceSupport[service].siteIds) ids.add(siteId);
    }
    return ids;
  }, [services, siteServiceSupport]);

  const { supportedSites, unsupportedSites } = useMemo(
    () =>
      groupSitesByServiceSupport(
        reachableOrPendingSites,
        supportedSiteIds,
        hideSelectedUnsupportedSite ? currentSite?.id : undefined
      ),
    [currentSite?.id, hideSelectedUnsupportedSite, reachableOrPendingSites, supportedSiteIds]
  );

  // Listing every service is the global picker, where naming them all would only add noise.
  const listsAllServices = services.length === ALL_SITE_SERVICES.length;
  const isServiceSpecificPicker = services.length > 0 && !listsAllServices;
  const showSupportedGroup = supportedSites.length > 0 || isServiceSpecificPicker;
  const showUnsupportedGroup = unsupportedSites.length > 0 || isServiceSpecificPicker;
  const showDisconnectedGroup = showUnReachableSites && unhealthySites.length > 0;
  const showGroupHeadings =
    isServiceSpecificPicker ||
    Number(showSupportedGroup) + Number(showUnsupportedGroup) + Number(showDisconnectedGroup) > 1;

  const supportedLabel = !showGroupHeadings
    ? undefined
    : listsAllServices
      ? t('onlineSitesWithServices')
      : t('selectASiteWithService', { service: serviceList });
  const unsupportedLabel = !showGroupHeadings
    ? undefined
    : listsAllServices
      ? t('onlineSitesWithoutServices')
      : unsupportedSites.length === 0
        ? t('noOtherConnectedSites')
        : t('otherSitesWithoutService', { service: serviceList });

  return (
    <FlexAligner direction='column' gap={responsive.spacing['7xlarge']} alignment='top-left'>
      {showSupportedGroup && (
        <SiteCardsGroup label={supportedLabel} items={supportedSites} onSelect={onSelect} />
      )}
      <FlexAligner direction='column' gap={responsive.spacing['4xlarge']} alignment='top-left'>
        {showUnsupportedGroup && (
          <SiteCardsGroup label={unsupportedLabel} items={unsupportedSites} onSelect={onSelect} />
        )}
        {showDisconnectedGroup && (
          <SiteCardsGroup
            label={showGroupHeadings ? t('disconnected') : undefined}
            items={unhealthySites}
            onSelect={onSelect}
          />
        )}
      </FlexAligner>
    </FlexAligner>
  );
};
